import { Component, CSSProperties, createRef, ReactNode, RefObject } from 'react';
import styles from './styles.scss';
import cn from 'classnames';
import { isBrowser } from 'utils';

type ElementId = string;
type Parent = ElementId | RefObject<HTMLElement>;
export type Positions = 'top' | 'right' | 'bottom' | 'left';
type StyleValues = Pick<
  CSSProperties,
  'top' | 'left' | 'right' | 'width' | 'transform' | 'visibility'
>;

interface OwnProps {
  parent: Parent;
  className?: string;
  text: ReactNode;
  position?: Positions;
  arrow?: true;
}

interface OwnState {
  dialogStyle: StyleValues;
}

const TOOL_TIP_ICON_WIDTH = 2;
const TOOL_TIP_ICON_WIDTH_HALF = TOOL_TIP_ICON_WIDTH / 2;

const transforms: { [key: string]: string } = {
  top: `calc(-50% + ${TOOL_TIP_ICON_WIDTH_HALF}rem), -100%`,
  bottom: `calc(-50% + ${TOOL_TIP_ICON_WIDTH_HALF}rem), ${TOOL_TIP_ICON_WIDTH}rem`,
  right: `${TOOL_TIP_ICON_WIDTH}rem, calc(-50% + ${TOOL_TIP_ICON_WIDTH_HALF}rem)`,
  left: `-100%, calc(-50% + ${TOOL_TIP_ICON_WIDTH_HALF}rem)`
};

const getTransform = (p?: Positions) => `translate(${transforms[p || 'top']})`;

export default class Dialog extends Component<OwnProps, OwnState> {
  private dialog: RefObject<HTMLDivElement>;
  constructor(props: OwnProps) {
    super(props);
    this.dialog = createRef();
  }

  public componentDidMount() {
    this.forceUpdate();
  }

  public render() {
    const {
      props: { text, className, arrow }
    } = this;
    const position = this.calculatePosition();

    return (
      <div className={cn(styles.base, styles[position])} style={this.getWrapperStyle()}>
        <div
          className={cn(styles.dialog, className)}
          style={this.getDialogStyle()}
          ref={this.dialog}
        >
          {text}
        </div>
        {arrow && <span className={cn(styles.arrow, styles[position])} />}
      </div>
    );
  }

  private calculatePosition = () => {
    const position = this.props.position || 'top';
    if (!isBrowser()) {
      return position;
    }
    const parent = this.getParentEl(this.props.parent);
    const dialog = this.getParentEl(this.dialog);

    if (parent === null || dialog === null) {
      return position;
    }

    const parentRect = parent.getBoundingClientRect();
    const dialogRect = dialog.getBoundingClientRect();

    if (position === 'top' && parentRect.top < dialogRect.height) {
      return 'bottom';
    }

    const bottom = parentRect.bottom + dialogRect.height;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    if (position === 'bottom' && bottom > windowHeight) {
      return 'top';
    }

    return position;
  };

  private getWrapperStyle = (): Pick<
    CSSProperties,
    'top' | 'left' | 'transform' | 'visibility'
  > => {
    const ref = this.getParentEl(this.props.parent);
    const { position } = this.props;

    if (!ref || !isBrowser()) {
      return { visibility: 'hidden' };
    }

    if (position === 'top' || position === 'bottom') {
      const pos = this.calculatePosition();
      return {
        transform: getTransform(pos)
      };
    }

    return {
      transform: getTransform(position)
    };
  };

  private getDialogStyle = (): StyleValues => {
    const PADDING = 5;
    const ref = this.getParentEl(this.dialog);
    if (!ref || !isBrowser()) {
      return { visibility: 'hidden' };
    }
    const { left, right } = ref.getBoundingClientRect();
    const clientWidth =
      (document.documentElement && document.documentElement.clientWidth) ||
      document.body.clientWidth ||
      window.innerWidth;
    const leftOffset = left < 0;
    const rightOffset = right > clientWidth;

    const styles: StyleValues = {};

    if (leftOffset) {
      styles.left = PADDING + Math.abs(left);
    } else if (rightOffset) {
      const difference = right - clientWidth;
      styles.right = difference + PADDING;
    }
    return styles;
  };

  private getParentEl = (parent: Parent) => {
    return typeof parent === 'string' ? document.querySelector(parent) : parent.current;
  };
}
